const trimOr = (value: string | undefined, fallback: string) => {
  const next = value?.trim()
  return next && next.length > 0 ? next : fallback
}

const optionalEnv = (value: string | undefined) => value?.trim() ?? ''

const designPortfolioBaseUrl = trimOr(import.meta.env.VITE_DESIGN_PORTFOLIO_BASE_URL, '/design')

const linkedInUrl = trimOr(import.meta.env.VITE_LINKEDIN_URL, 'https://www.linkedin.com/in/geknz/')

export const siteConfig = {
  /** Public site / brand label (OG site_name, publisher). */
  name: trimOr(import.meta.env.VITE_SITE_NAME, 'Gosha Knyazhev'),
  /** Person shown on the page and in Article JSON-LD. */
  authorName: trimOr(import.meta.env.VITE_AUTHOR_NAME, 'Gosha Knyazhev'),
  /** Subtitle under the name (e.g. case study About row). Env: `VITE_AUTHOR_TITLE`. */
  authorTitle: trimOr(import.meta.env.VITE_AUTHOR_TITLE, 'AI-native designer'),
  description: trimOr(
    import.meta.env.VITE_SITE_DESCRIPTION,
    'AI-native designer building hyperpersonalized web, sales assets, and buyer-specific experiences for B2B teams.',
  ),
  /**
   * Open Graph / Twitter title for the home page (keep under ~60 characters where possible).
   * Document `<title>` stays longer for the tab; crawlers use this for link previews.
   */
  ogShareTitle: trimOr(
    import.meta.env.VITE_OG_SHARE_TITLE,
    'Gosha Knyazhev — AI flows for B2B sales',
  ),
  siteUrl: trimOr(import.meta.env.VITE_SITE_URL, 'https://gosha.ee').replace(/\/+$/, ''),
  faviconPath: '/favicon.svg',
  /**
   * Organization logo for JSON-LD / Knowledge Graph (absolute URL or site path).
   * Prefer a square PNG at least 112×112px (512×512 recommended). Env: `VITE_ORGANIZATION_LOGO_URL`.
   * Empty → `/favicon.svg`.
   */
  organizationLogoUrl: optionalEnv(import.meta.env.VITE_ORGANIZATION_LOGO_URL),
  /** Default Open Graph / Twitter image (PNG recommended for social crawlers). Env: `VITE_OG_IMAGE_PATH`. */
  ogImagePath: trimOr(import.meta.env.VITE_OG_IMAGE_PATH, '/og/og-preview.png'),
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogImageAlt: trimOr(
    import.meta.env.VITE_OG_IMAGE_ALT,
    'Gosha Knyazhev — AI-native designer for personalized B2B sales experiences.',
  ),
  /**
   * Google Tag Manager container (e.g. `GTM-XXXXXXX`). When set, GTM loads instead of direct GA4.
   * Configure GA4 inside GTM, or leave empty and use `gaMeasurementId`.
   */
  gtmContainerId: optionalEnv(import.meta.env.VITE_GTM_CONTAINER_ID),
  /** Google Analytics 4 measurement ID (e.g. `G-XXXXXXXXXX`). Used when `gtmContainerId` is empty. */
  gaMeasurementId: optionalEnv(import.meta.env.VITE_GA_MEASUREMENT_ID),
  /** [Google Search Console](https://search.google.com/search-console) HTML tag verification token. */
  googleSiteVerification: optionalEnv(import.meta.env.VITE_GOOGLE_SITE_VERIFICATION),
  /** Scheduling link (Notion Calendar, Calendly, etc.). Env: `VITE_CALENDLY_URL`. */
  calendlyUrl: trimOr(
    import.meta.env.VITE_CALENDLY_URL,
    'https://calendar.notion.so/meet/geknz/gosha',
  ),
  linkedInUrl,
  /**
   * Case study About row: external link (new tab), same default styling as the homepage LinkedIn line.
   * Override with `VITE_AUTHOR_EXTERNAL_URL` / `VITE_AUTHOR_EXTERNAL_LABEL`.
   */
  authorExternalUrl: trimOr(import.meta.env.VITE_AUTHOR_EXTERNAL_URL, linkedInUrl),
  authorExternalLabel: trimOr(import.meta.env.VITE_AUTHOR_EXTERNAL_LABEL, 'LinkedIn'),
  contactEmail: trimOr(import.meta.env.VITE_CONTACT_EMAIL, 'gknjazev@gmail.com'),
  /** PDF or page URL for the CV button in site navigation. */
  cvUrl: trimOr(
    import.meta.env.VITE_CV_URL,
    'https://geknz.notion.site/Georgi-Knja-ev-11928219390d80f688f7e2272a0a9b82?pvs=4',
  ),
  whatsappUrl: trimOr(import.meta.env.VITE_WHATSAPP_URL, 'https://wa.me/+3725014501'),
  telegramUrl: trimOr(import.meta.env.VITE_TELEGRAM_URL, 'https://t.me/geknz'),

  /**
   * Design portfolio and case studies on the primary site.
   * Override base with `VITE_DESIGN_PORTFOLIO_BASE_URL` if needed.
   */
  designPortfolioBaseUrl,
  workIndexUrl: '/work/',
  designPortfolioCaseUrls: {
    iq: `${designPortfolioBaseUrl}/iq/`,
    clipflip: `${designPortfolioBaseUrl}/sides/#clipflip`,
    kuludAi: `${designPortfolioBaseUrl}/kulud.ai/`,
  } as const,
}
